import React from "react";

/**
 * TraitDots
 * Compact colored-dot row for a worker's character traits.
 * Hover any dot for a tooltip showing the trait name + description.
 *
 * Props:
 *   traits: [{ slug, name, dot_color, description }]  — required
 *   size:   "xs" | "sm" | "md"   — dot size (default sm)
 *   showLabels: boolean          — render trait names next to each dot
 *   className?: string
 */
const DOT_SIZE = { xs: "w-1.5 h-1.5", sm: "w-2 h-2", md: "w-2.5 h-2.5" };

export default function TraitDots({
    traits,
    size = "sm",
    showLabels = false,
    className = "",
}) {
    if (!traits || traits.length === 0) return null;
    return (
        <div
            className={`inline-flex items-center gap-2 flex-wrap ${className}`}
            data-testid="trait-dots"
        >
            {traits.map((t) => (
                <span
                    key={t.slug}
                    title={`${t.name} — ${t.description}`}
                    data-testid={`trait-dot-${t.slug}`}
                    className={`inline-flex items-center gap-1 ${showLabels ? "" : "cursor-help"}`}
                >
                    <span
                        className={`${DOT_SIZE[size]} rounded-full flex-shrink-0`}
                        style={{ backgroundColor: t.dot_color }}
                    />
                    {showLabels && (
                        <span className="text-[11px] text-gray-600">{t.name}</span>
                    )}
                </span>
            ))}
        </div>
    );
}


/**
 * TraitCards
 * Detailed stacked cards showing dot + name + description.
 * Used on the worker page left rail "Character" sub-block.
 */
export function TraitCards({ traits, className = "" }) {
    if (!traits || traits.length === 0) return null;
    return (
        <ul className={`space-y-2 ${className}`} data-testid="trait-cards">
            {traits.map((t) => (
                <li
                    key={t.slug}
                    data-testid={`trait-card-${t.slug}`}
                    className="flex items-start gap-2.5"
                >
                    <span
                        className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: t.dot_color }}
                    />
                    <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-gray-900 leading-tight">{t.name}</div>
                        <div className="text-[11.5px] text-gray-500 leading-snug mt-0.5">{t.description}</div>
                    </div>
                </li>
            ))}
        </ul>
    );
}
